import React, { useCallback } from "react";
import BillingListImpl from "./BillingList/index.js";

const hasId = (value) => value !== null && value !== undefined && value !== "";

const firstNonEmpty = (...values) => {
  for (const value of values) {
    if (value !== null && value !== undefined && String(value).trim() !== "") {
      return value;
    }
  }
  return "";
};

const normalizeSupplierSource = (value, supplierInfo = {}) => {
  const normalized = String(value || "").trim().toLowerCase();

  if (["asi-supplier", "asi", "supplier"].includes(normalized)) {
    return "asi-supplier";
  }

  if (["core-supplier", "core", "internal", "internal-supplier", "customsupplier", "custom-supplier"].includes(normalized)) {
    return "core-supplier";
  }

  if (supplierInfo?.AsiNumber || supplierInfo?.asiNumber) {
    return "asi-supplier";
  }

  return "";
};

const resolveSourceFromPoItems = (items = [], supplierId = null) => {
  if (!Array.isArray(items) || !items.length || !hasId(supplierId)) return "";

  const matchingSources = items
    .filter((item) => {
      const itemSupplierId =
        item?.SupplierId ??
        item?.supplierId ??
        item?.supplier?.id ??
        item?.Supplier?.id ??
        null;

      return hasId(itemSupplierId) && String(itemSupplierId) === String(supplierId);
    })
    .map((item) => normalizeSupplierSource(
      firstNonEmpty(
        item?.SupplierSource,
        item?.supplierSource,
        item?.Source,
        item?.source,
        item?.supplier?.SupplierSource,
        item?.supplier?.supplierSource,
        item?.Supplier?.SupplierSource,
        item?.Supplier?.supplierSource
      ),
      item?.supplier || item?.Supplier || item
    ))
    .filter(Boolean);

  const uniqueSources = Array.from(new Set(matchingSources));
  return uniqueSources.length === 1 ? uniqueSources[0] : "";
};

export const normalizeBillPartySeed = (seed = {}) => {
  if (!seed || typeof seed !== "object") return seed;

  const supplierInfo = seed?.supplierInfo && typeof seed.supplierInfo === "object"
    ? seed.supplierInfo
    : {};

  const supplierId =
    seed?.supplierId ??
    supplierInfo?.supplierId ??
    supplierInfo?.vendorSupplierId ??
    supplierInfo?.id ??
    null;

  const decoratorId =
    seed?.decoratorId ??
    seed?.decoratorInfo?.id ??
    supplierInfo?.decoratorId ??
    null;

  const shippingAccountId =
    seed?.shippingAccountId ??
    supplierInfo?.shippingAccountId ??
    supplierInfo?.shippingAccount?.id ??
    null;

  const poType = String(seed?.poType || supplierInfo?.type || "")
    .trim()
    .toLowerCase();

  const explicitPartyType = String(seed?.billPartySourceType || seed?.billPartyType || "")
    .trim()
    .toLowerCase();

  const isDecoratorPo =
    poType === "different_supplier_decoration" ||
    explicitPartyType === "decorator" ||
    hasId(decoratorId);

  let billPartyType = "supplier";

  if (explicitPartyType === "other-service" || (supplierInfo?.party?.partyType === "other-service" && supplierInfo?.party?.sourceType && supplierInfo?.party?.sourceId)) {
    billPartyType = "other-service";
  } else if (isDecoratorPo) {
    billPartyType = "decorator";
  } else if (hasId(supplierId)) {
    billPartyType = "supplier";
  } else if (explicitPartyType === "shipping-account" && hasId(shippingAccountId)) {
    billPartyType = "shipping-account";
  } else if (!hasId(supplierId) && !hasId(decoratorId) && hasId(shippingAccountId)) {
    billPartyType = "shipping-account";
  }

  const supplierName = firstNonEmpty(
    supplierInfo?.supplierName,
    supplierInfo?.SupplierName,
    supplierInfo?.Name,
    seed?.supplierName,
    seed?.billSupplierInfo?.Name
  );

  const explicitSupplierSource = normalizeSupplierSource(
    firstNonEmpty(
      seed?.supplierSource,
      seed?.SupplierSource,
      supplierInfo?.supplierSource,
      supplierInfo?.SupplierSource,
      supplierInfo?.source,
      supplierInfo?.Source
    ),
    supplierInfo
  );

  const supplierSource =
    explicitSupplierSource ||
    resolveSourceFromPoItems(seed?.items || seed?._poItems || [], supplierId);

  const decoratorName = firstNonEmpty(
    supplierInfo?.decoratorName,
    supplierInfo?.decoratorTitle,
    seed?.decoratorName,
    seed?.decoratorInfo?.title
  );

  const shippingAccountName = firstNonEmpty(
    seed?.shippingAccount?.accountName,
    supplierInfo?.shippingAccount?.accountName,
    supplierInfo?.shippingAccount?.name,
    seed?.shippingAccountName
  );

  const poPartyName = billPartyType === "decorator"
    ? decoratorName
    : billPartyType === "shipping-account"
      ? shippingAccountName
      : billPartyType === "other-service"
        ? firstNonEmpty(supplierInfo?.party?.partyName, supplierName)
        : supplierName;

  const normalizedSupplierInfo = billPartyType === "supplier"
    ? {
        ...supplierInfo,
        supplierId: hasId(supplierId) ? supplierId : supplierInfo?.supplierId,
        supplierName: supplierName || supplierInfo?.supplierName || "",
        ...(supplierSource
          ? {
              supplierSource,
              SupplierSource: supplierSource,
            }
          : {}),
      }
    : supplierInfo;

  return {
    ...seed,
    billPartyType,
    billPartySourceType: billPartyType,
    poPartyName: poPartyName || seed?.poPartyName || "",
    ...(billPartyType === "supplier"
      ? {
          supplierId,
          supplierName,
          supplierInfo: normalizedSupplierInfo,
          ...(supplierSource
            ? {
                supplierSource,
                SupplierSource: supplierSource,
              }
            : {}),
        }
      : {}),
  };
};

const BillingList = (props) => {
  const setPoSeedData = props?.setPoSeedData;

  const setNormalizedPoSeedData = useCallback((seedOrUpdater) => {
    if (typeof setPoSeedData !== "function") return;

    if (typeof seedOrUpdater === "function") {
      setPoSeedData((previous) => normalizeBillPartySeed(seedOrUpdater(previous)));
      return;
    }

    setPoSeedData(normalizeBillPartySeed(seedOrUpdater));
  }, [setPoSeedData]);

  return (
    <BillingListImpl
      {...props}
      setPoSeedData={setNormalizedPoSeedData}
    />
  );
};

export default BillingList;
